import { createClient } from '@supabase/supabase-js'

// Get Supabase URL and anon key from environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://yviqlmtoftjjzgrrcowd.supabase.co'
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
const supabaseServiceRoleKey = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY
const siteUrl = import.meta.env.VITE_SITE_URL || 'https://fmdpro.app'

if (!supabaseAnonKey) {
  throw new Error('VITE_SUPABASE_ANON_KEY is required')
}

// Create Supabase clients
export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Create admin client with service role key if available
export const supabaseAdmin = supabaseServiceRoleKey 
  ? createClient(supabaseUrl, supabaseServiceRoleKey, {
      auth: {
        autoRefreshToken: false,
        persistSession: false
      }
    })
  : null // Don't fallback to regular client for admin operations

if (!supabaseAdmin) {
  console.warn('VITE_SUPABASE_SERVICE_ROLE_KEY not found. Admin operations will not work.')
}

/**
 * Check if the browser is online
 * @returns boolean indicating online status
 */
export const isOnline = (): boolean => {
  return typeof navigator !== 'undefined' && navigator.onLine
}

/**
 * Handle Supabase errors consistently
 * @param error - The error object from Supabase
 * @returns A standardized error object
 */
export const handleSupabaseError = (error: any) => {
  console.error('Supabase error:', error)
  
  // Create a standardized error response
  return {
    success: false,
    error: error instanceof Error ? error.message : 'Unknown error',
    code: error?.code || 'UNKNOWN',
    details: error?.details || null
  }
}

/**
 * Utility function to provide offline fallback for Supabase operations
 * @param onlineOperation - Function to execute when online
 * @param offlineFallback - Function to execute when offline
 * @returns Result of either the online operation or offline fallback
 */
export const withOfflineFallback = async <T>(
  onlineOperation: () => Promise<T>,
  offlineFallback: () => Promise<T> | T
): Promise<T> => {
  // If we're offline, use the fallback immediately
  if (!isOnline()) {
    console.log('Offline mode: using fallback')
    return await Promise.resolve(offlineFallback())
  }
  
  try {
    // Try the online operation
    return await onlineOperation()
  } catch (error) {
    console.warn('Online operation failed, using offline fallback:', error)
    return await Promise.resolve(offlineFallback())
  }
}

/**
 * Check if a table exists in the Supabase database
 * @param tableName - The name of the table to check
 * @returns Promise resolving to boolean indicating if table exists
 */
export async function checkTableExists(tableName: string): Promise<boolean> {
  try {
    // First try using information_schema which is more widely accessible
    const { data, error } = await supabase.rpc('table_exists', { 
      table_name: tableName 
    })

    if (error) {
      // If the RPC function doesn't exist, fall back to a direct query
      if (error.code === 'PGRST202') {
        console.warn('table_exists function not found, falling back to direct query')
        return checkTableExistsFallback(tableName)
      }
      console.error(`Error checking if table ${tableName} exists:`, error)
      return false
    }

    return data === true
  } catch (err) {
    console.error(`Exception checking if table ${tableName} exists:`, err)
    return false
  }
}

/**
 * Fallback method to check if a table exists
 * Uses a simple SELECT query that will fail if the table doesn't exist
 */
async function checkTableExistsFallback(tableName: string): Promise<boolean> {
  try {
    // Use a simple query that will fail if the table doesn't exist
    const { error } = await supabase
      .from(tableName)
      .select('*')
      .limit(1)

    // If we get an error about the relation not existing, return false
    if (error?.code === '42P01') { // 42P01 = undefined_table
      return false
    }
    
    // Any other error might mean the table exists but there was another issue
    if (error) {
      console.error(`Error in fallback table check for ${tableName}:`, error)
      return true // Assume table exists if we're not sure
    }
    
    return true
  } catch (err) {
    console.error(`Exception in fallback table check for ${tableName}:`, err)
    return false
  }
}

/**
 * Refreshes the schema cache to ensure up-to-date column information.
 * This function falls back gracefully if the RPC method doesn't exist.
 */
export async function refreshSchemaCache(): Promise<void> {
  try {
    // Check if there's a cache to refresh
    console.log('Attempting to refresh schema cache...');
    
    // Use a direct SELECT query instead of RPC call that might not exist
    await supabase.from('contracts').select('id').limit(1);
    
    console.log('Schema cache refreshed or bypassed successfully');
    return;
  } catch (err) {
    console.warn('Schema cache refresh failed, continuing anyway:', err);
    // Fail silently - this is just a cache refresh
    return;
  }
}